import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';

import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar } from '@angular/material/snack-bar';

import { skip, Subscription } from 'rxjs';

import { PhonepeUpiSearchService } from '../../../services/phonepe-upi/phonepe-upi.service';
import { SearchPollingService } from '../../../services/search-polling/search-polling.service';
import { UtilsService } from '../../../services/utils/utils.service';
import { HeaderComponent } from '../../common/header/header.component';
import { SidebarComponent } from '../../common/slidebar/sidebar.components';

@Component({
  selector: 'app-osint-phonepe-upi-search-result',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    HeaderComponent,
    SidebarComponent,
  ],
  templateUrl: './osint-phonepe-upi-search-result.component.html',
  styleUrl: './osint-phonepe-upi-search-result.component.css',
})
export class OsintPhonepeUpiSearchResultComponent implements OnInit, OnDestroy {
  jobId: string = '';
  searchId: string = '';
  selectedSearchResult: any;
  phonepeUpiData: any = null;
  noRecords = false;

  loadingCompletedSearchResult: boolean = false;
  loadingPolledSearchResult: boolean = false;

  jobResultSubscription?: Subscription;

  get isLoadingResults(): boolean {
    const hasResults = !!this.phonepeUpiData;
    return (
      (this.loadingCompletedSearchResult || this.loadingPolledSearchResult) &&
      !hasResults &&
      !this.noRecords
    );
  }

  get hasIdentitySection(): boolean {
    const d = this.phonepeUpiData;
    return (
      this.hasValue(d?.name) ||
      this.hasValue(d?.vpa) ||
      this.hasValue(d?.user_type) ||
      this.hasValue(d?.verified_merchant)
    );
  }

  get hasBankSection(): boolean {
    const d = this.phonepeUpiData;
    return (
      this.hasValue(d?.ifsc) ||
      this.hasValue(d?.bank) ||
      this.hasValue(d?.bank_code) ||
      this.hasValue(d?.branch) ||
      this.hasValue(d?.city) ||
      this.hasValue(d?.state) ||
      this.hasValue(d?.district) ||
      this.hasValue(d?.centre) ||
      this.hasValue(d?.address) ||
      this.hasValue(d?.contact) ||
      this.hasValue(d?.micr) ||
      this.hasValue(d?.swift) ||
      this.hasValue(d?.iso3166)
    );
  }

  get hasPaymentRailsSection(): boolean {
    const d = this.phonepeUpiData;
    return (
      this.hasValue(d?.upi_enabled) ||
      this.hasValue(d?.neft_enabled) ||
      this.hasValue(d?.imps_enabled) ||
      this.hasValue(d?.rtgs_enabled)
    );
  }

  get hasPhoto(): boolean {
    return !!this.displayablePhotoUrl;
  }

  /** Absolute http(s) photo only — vendor templates like `/photo/%d/%d/...` break Vite decode. */
  get displayablePhotoUrl(): string | null {
    return this.toDisplayablePhotoUrl(this.phonepeUpiData?.photo);
  }

  notes: string = '';

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private snackBar: MatSnackBar,
    private searchPollingService: SearchPollingService,
    private phonepeUpiSearchService: PhonepeUpiSearchService,
    public utilsService: UtilsService,
  ) {}

  ngOnInit() {
    this.selectedSearchResult = history.state?.searchResult;
    this.jobId = this.route.snapshot.paramMap.get('id') || '';

    if (!this.selectedSearchResult && !this.jobId) {
      this.router.navigate(['/phonepe-upi-search']);
      return;
    }

    if (this.selectedSearchResult) {
      this.searchId = this.selectedSearchResult.phonepe_upi_search_id;

      if (this.selectedSearchResult.done) {
        this.getCompletedSearchResult();
      } else {
        this.pollForSearchResult();
      }
    } else if (this.jobId) {
      this.pollForSearchResult();
    }
  }

  ngOnDestroy() {
    this.jobResultSubscription?.unsubscribe();
  }

  onBackButtonClick() {
    this.router.navigate(['/phonepe-upi-search']);
  }

  getCompletedSearchResult() {
    if (!this.searchId) {
      return;
    }

    this.loadingCompletedSearchResult = true;

    this.phonepeUpiSearchService
      .getCompletedSearchResult(this.searchId)
      .subscribe({
        next: (response) => {
          const rawSearchResultData = response?.data;
          const completedSearchResult = rawSearchResultData?.data_list?.[0];

          if (!completedSearchResult) {
            this.noRecords = true;
            this.loadingCompletedSearchResult = false;
            this.loadingPolledSearchResult = false;
            return;
          }

          this.selectedSearchResult = {
            ...this.selectedSearchResult,
            ...completedSearchResult,
            searched_upi:
              completedSearchResult?.phonepe_upi_data?.vpa ||
              this.selectedSearchResult?.searched_upi,
          };

          this.notes =
            rawSearchResultData?.notes || completedSearchResult?.notes || '';
          this.phonepeUpiData =
            completedSearchResult?.phonepe_upi_data || null;
          this.noRecords =
            !this.phonepeUpiData?.found &&
            !this.hasAnyField(this.phonepeUpiData);

          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;
        },
        error: (error) => {
          console.error(error);
          this.loadingCompletedSearchResult = false;
          this.loadingPolledSearchResult = false;
        },
      });
  }

  private hasAnyField(data: any): boolean {
    if (!data) return false;
    return Boolean(
      data.name ||
        data.vpa ||
        data.user_type ||
        data.ifsc ||
        data.bank ||
        data.photo,
    );
  }

  hasValue(value: unknown): boolean {
    if (value === null || value === undefined) return false;
    if (typeof value === 'boolean') return true;
    if (typeof value === 'number') return true;
    if (typeof value === 'string') {
      const trimmed = value.trim();
      return trimmed.length > 0 && trimmed !== '()';
    }
    return true;
  }

  private toDisplayablePhotoUrl(photo: unknown): string | null {
    if (typeof photo !== 'string') return null;
    const trimmed = photo.trim();
    if (!trimmed) return null;
    // Unsubstituted printf / invalid percent sequences (e.g. %d) → URIError in Angular Vite.
    if (/%(?![0-9A-Fa-f]{2})/.test(trimmed)) return null;
    try {
      const url = new URL(trimmed);
      if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
      return trimmed;
    } catch {
      return null;
    }
  }

  formatBool(value: unknown): string {
    if (typeof value === 'boolean') return value ? 'Yes' : 'No';
    if (value === null || value === undefined || value === '') return '';
    return String(value);
  }

  pollForSearchResult() {
    if (!this.jobId) {
      this.jobId = this.selectedSearchResult?.job_id;
    }

    if (!this.jobId) {
      console.error('No job ID available for polling');
      return;
    }

    this.searchPollingService.startPolling([this.jobId], (ids) =>
      this.phonepeUpiSearchService.getSearchResults(ids),
    );

    this.jobResultSubscription?.unsubscribe();
    this.loadingPolledSearchResult = true;

    this.jobResultSubscription = this.searchPollingService
      .getJobResult(this.jobId)
      .pipe(skip(1))
      .subscribe({
        next: (response) => {
          if (!response) {
            return;
          }

          if (response.phonepe_upi_search_id && !this.searchId) {
            this.searchId = response.phonepe_upi_search_id;
          }

          this.selectedSearchResult = {
            ...this.selectedSearchResult,
            done: response.done || false,
            status: response.status || 'pending',
            searched_upi:
              response?.searched_upi ||
              this.selectedSearchResult?.searched_upi,
            phonepe_upi_search_id:
              response?.phonepe_upi_search_id ||
              this.selectedSearchResult?.phonepe_upi_search_id,
          };

          if (response.done === true || response.status === 'completed') {
            this.getCompletedSearchResult();
          }
        },
        error: (error) => {
          console.error(error);
          this.loadingPolledSearchResult = false;
        },
      });
  }

  onUpdateNotesButtonClick(): void {
    const resultId = this.selectedSearchResult?.result_id;

    if (!this.notes.trim() || !resultId) {
      this.snackBar.open(
        'Please enter notes and ensure result ID is available.',
        'Close',
        {
          duration: 3000,
          panelClass: ['snackbar-error'],
        },
      );
      return;
    }

    const payload = {
      result_id: resultId,
      notes: this.notes.trim(),
    };

    this.phonepeUpiSearchService.updatePhonepeUpiNotes(payload).subscribe({
      next: () => {
        this.snackBar.open('Notes updated successfully!', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-success'],
        });
      },
      error: () => {
        this.snackBar.open('Failed to update notes.', 'Close', {
          duration: 3000,
          panelClass: ['snackbar-error'],
        });
      },
    });
  }
}
